import { useCallback, useEffect, useMemo, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import DraggableQuestionTable from "@/components/DraggableQuestionTable/DraggableQuestionTable";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import { TableCellTooltip } from "@/components/Tooltip";
import PencilIcon from "@/components/SVGComponents/PencilIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import { useSearchPagination } from "@/hooks/useSearchPagination";
import { useStringQueryParamState } from "@/hooks/useStringQueryParamState";
import { checkPermission, captalizeEachWord } from "@/lib/utils";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import toast from "@/lib/toast";
import QuestionnaireService from "@/services/api/questionnaire";
import { getQuestions, deleteQuestion, updateQuestionsOrder } from "@/store/slices/QuestionSlice";
import type { GetQuestionParams, QuestionField, QuestionOrderItem, UserType } from "@/types/question";
import { DEFAULT_QUESTION_PAGE, QUESTION_TABLE_HEIGHT } from "@/types/question";
import type { Column } from "@/types/common";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import Button from "@/components/Button/Button";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import ChevronDownIcon from "@/components/SVGComponents/ChevronDownIcon";
import MobileSearchBar from "@/components/MobileSearchBar/MobileSearchBar";
import FilterChips from "@/components/FilterChips/FilterChips";
import MobileList, { MobileListPanel, MobileListRow, SortableMobileList } from "@/components/MobileList/MobileList";
import AddQuestionModal from "./components/AddQuestionModal";
import { findOrderViolation, getDependentQuestions, getOrderViolationMessage } from "./lib/questionDependencies";
import styles from "./QuestionnaireManagement.module.scss";

const TABS = [
  { label: "Founder", value: "founder" },
  { label: "Investor", value: "investor" },
];

const QuestionnaireManagement = () => {
  const { debouncedSearch, search, setSearch, pageSelected, setPageSelected, rowsPerPageValue } = useSearchPagination({
    syncPageWithQuery: false,
    syncRowsPerPageWithQuery: false,
  });
  const [activeTab, setActiveTab] = useStringQueryParamState("tab", "founder");
  const [editingQuestion, setEditingQuestion] = useState<QuestionField | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<QuestionField | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const dispatch = useAppDispatch();
  const { isFetching, totalQuestions, questions } = useAppSelector((state) => state.question);

  const questionUserType: UserType = activeTab === "investor" ? "INVESTOR" : "FOUNDER";
  const hasMoreQuestions = pageSelected * rowsPerPageValue < totalQuestions;
  const isInitialLoadingQuestions = isFetching && pageSelected === DEFAULT_QUESTION_PAGE && !isReordering;
  const isLoadingMoreQuestions = isFetching && pageSelected > DEFAULT_QUESTION_PAGE && !isReordering;

  const buildQuestionParams = useCallback(
    (overrides: Partial<GetQuestionParams> = {}): GetQuestionParams => ({
      search: debouncedSearch ?? "",
      pageNo: pageSelected,
      perPage: rowsPerPageValue,
      userType: questionUserType,
      ...overrides,
    }),
    [debouncedSearch, pageSelected, questionUserType, rowsPerPageValue],
  );

  const buildLoadedWindowParams = useCallback(
    (): GetQuestionParams =>
      buildQuestionParams({ pageNo: DEFAULT_QUESTION_PAGE, perPage: Math.max(rowsPerPageValue, pageSelected * rowsPerPageValue) }),
    [buildQuestionParams, pageSelected, rowsPerPageValue],
  );

  useEffect(() => {
    if (!checkPermission("getAllQuestions")) return;
    dispatch(getQuestions(buildQuestionParams()));
  }, [buildQuestionParams, dispatch]);

  const handleTabChange = useCallback(
    (nextTab: string) => {
      setPageSelected(DEFAULT_QUESTION_PAGE);
      setActiveTab(nextTab);
    },
    [setActiveTab, setPageSelected],
  );

  // `useSearchPagination`'s `search` is `string | null` (URL-param backed);
  // `CustomHeader` wants a plain `Dispatch<SetStateAction<string>>` — this
  // bridges the two without ever needing a `null` on the header's side.
  const handleSearchChange: Dispatch<SetStateAction<string>> = useCallback(
    (value) => {
      setSearch((prev) => (typeof value === "function" ? value(prev ?? "") : value));
    },
    [setSearch],
  );

  const handleLoadMore = useCallback(() => {
    if (isFetching || !hasMoreQuestions) return;
    setPageSelected((currentPage) => currentPage + 1);
  }, [hasMoreQuestions, isFetching, setPageSelected]);

  const handleDelete = () => {
    if (!deleteTarget) return;

    dispatch(deleteQuestion(String(deleteTarget.id)))
      .unwrap()
      .then(() => {
        toast.success("Question deleted successfully");
        return dispatch(getQuestions(buildLoadedWindowParams())).unwrap();
      })
      .catch(() => {
        // privateRequest's response interceptor already shows an error toast
        // (see services/axios.ts) — nothing more to do here.
      });
  };

  // Returns `false` (and saves nothing) when the new order would put a
  // dependent question before the question it depends on — the drag
  // tables use that to snap the row back.
  const handleReorder = (newData: QuestionField[]): boolean => {
    const violation = findOrderViolation(newData);
    if (violation) {
      toast.error(getOrderViolationMessage(violation));
      return false;
    }

    setIsReordering(true);
    // Every question in its new position, numbered from 1 — sent together
    // in one `PATCH /questions` request.
    const payload: QuestionOrderItem[] = newData.map((item, index) => ({
      questionId: item.id,
      displayOrder: index + 1,
    }));

    dispatch(updateQuestionsOrder(payload))
      .unwrap()
      .then(() => {
        toast.success("Order updated");
        return dispatch(getQuestions(buildLoadedWindowParams())).unwrap();
      })
      .catch(() => {
        // privateRequest's response interceptor already shows an error toast
        // (see services/axios.ts) — nothing more to do here.
      })
      .finally(() => {
        setIsReordering(false);
      });
    return true;
  };

  // Deleting a question other questions depend on would leave them
  // pointing at a question that no longer exists, so that's blocked until
  // their dependency is removed. Checked against the persona's full list
  // (not just the loaded window, which may not include every dependent).
  // Reads only the row's own `userType` — `columns` below is memoized once,
  // so anything read from page state here would go stale.
  const requestDelete = async (question: QuestionField) => {
    try {
      const result = await QuestionnaireService.getAllQuestions({
        pageNo: DEFAULT_QUESTION_PAGE,
        perPage: 1000,
        search: "",
        userType: question.userType,
      });
      const dependents = getDependentQuestions(question.id, result?.data?.results ?? []);
      if (dependents.length > 0) {
        const names = dependents.map((dependent) => `"${dependent.label}"`).join(", ");
        toast.error(
          `Can't delete — ${dependents.length === 1 ? "this question is" : `${dependents.length} questions are`} ` +
            `depending on it: ${names}. Remove that dependency first.`,
        );
        return;
      }
      setDeleteTarget(question);
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  const getRowActions = (question: QuestionField): RowAction[] => {
    const actions: RowAction[] = [];

    if (checkPermission("updateQuestion")) {
      actions.push({
        key: "edit",
        label: "Edit",
        icon: <PencilIcon />,
        onClick: () => {
          setEditingQuestion(question);
          setIsAddModalOpen(true);
        },
      });
    }

    if (checkPermission("deleteQuestion")) {
      actions.push({
        key: "delete",
        label: "Delete",
        icon: <CrossIcon withBg={false} stroke="currentColor" strokeWidth={1.5} width={14} height={14} />,
        tone: "danger",
        dividerBefore: actions.length > 0,
        onClick: () => requestDelete(question),
      });
    }

    return actions;
  };

  // Mobile stand-in for drag-to-reorder: swap with the neighbouring loaded
  // question, then save through the same `handleReorder` the desktop drag uses.
  const moveQuestion = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (isReordering || targetIndex < 0 || targetIndex >= questions.length) return;
    const reordered = [...questions];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex]!, reordered[index]!];
    handleReorder(reordered);
  };

  const getMobileRowActions = (question: QuestionField, index: number): RowAction[] => {
    const actions = getRowActions(question);
    if (!checkPermission("updateQuestion")) return actions;

    const moveActions: RowAction[] = [];
    if (index > 0) {
      moveActions.push({
        key: "moveUp",
        label: "Move up",
        icon: <ChevronDownIcon style={{ transform: "rotate(180deg)" }} />,
        onClick: () => moveQuestion(index, -1),
      });
    }
    if (index < questions.length - 1) {
      moveActions.push({
        key: "moveDown",
        label: "Move down",
        icon: <ChevronDownIcon />,
        onClick: () => moveQuestion(index, 1),
      });
    }

    return [...moveActions, ...actions];
  };

  const columns: Column<QuestionField>[] = useMemo(
    () => [
      { name: "", width: "16px" },
      {
        name: "Question",
        width: "26%",
        cell: (row) => <TableCellTooltip value={row?.label || "—"} copyable />,
      },
      { name: "Question Type", width: "12%", selector: (row) => captalizeEachWord(row?.fieldType) || "—" },
      { name: "Assign to", width: "10%", selector: (row) => captalizeEachWord(row?.userType) || "—" },
      { name: "Category", width: "12%", selector: (row) => row?.categoryName || "—" },
      { name: "Display order", width: "10%", center: true, selector: (row) => row?.displayOrder ?? "—" },
      { name: "Created by", width: "12%", selector: (row) => row?.createdBy || "—" },
      {
        name: "Action",
        width: "8%",
        cell: (row) => {
          const actions = getRowActions(row);
          return actions.length ? <RowActionsMenu actions={actions} ariaLabel={`Actions for ${row.label}`} /> : null;
        },
      },
    ],
    // `getRowActions` only reads its row plus state setters (and
    // `requestDelete`, which does the same), so the first render's copy
    // never goes stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const closeAddModal = () => {
    setIsAddModalOpen(false);
    setEditingQuestion(null);
  };

  const openExportModal = () => {
    if (!checkPermission("getAllQuestions")) return;
    setIsExportModalOpen(true);
  };

  const openAddModal = () => {
    if (!checkPermission("createQuestion")) return;
    setEditingQuestion(null);
    setIsAddModalOpen(true);
  };

  return (
    <div className={styles.main}>
      <AddQuestionModal
        isOpen={isAddModalOpen}
        onClose={closeAddModal}
        questionToEdit={editingQuestion}
        userType={questionUserType}
        onSaved={() => dispatch(getQuestions(buildLoadedWindowParams()))}
      />
      <ConfirmDeleteModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Question"
        message={
          <>
            Are you sure you want to delete <strong>{deleteTarget?.label || "this question"}</strong>? This action
            cannot be undone.
          </>
        }
      />

      {/* Desktop View */}
      <div className={styles.desktopView}>
        <DraggableQuestionTable<QuestionField>
          columns={columns}
          data={questions}
          containerHeight={QUESTION_TABLE_HEIGHT}
          progressPending={isInitialLoadingQuestions}
          header={{
            search: search ?? "",
            setSearch: handleSearchChange,
            searchPlaceholder: "Search questions",
            tabs: TABS,
            activeTab,
            onTabChange: handleTabChange,
            tabsPosition: "below",
            exportLabel: "EXPORT QUESTIONS",
            onExport: openExportModal,
            addButtonTitle: "Add Question",
            onAddClick: openAddModal,
          }}
          infiniteScrollProps={{
            enabled: true,
            hasMore: hasMoreQuestions,
            isLoadingMore: isLoadingMoreQuestions,
            loadingLabel: "Loading more questions...",
            onLoadMore: handleLoadMore,
          }}
          onRowReorder={handleReorder}
        />
      </div>

      {/* Mobile View: same search/tab/paging state as the table above.
          Drag-to-reorder is desktop-only. */}
      <div className={styles.mobileView}>
        <MobileListPanel
          actions={[
            { key: "export", label: "EXPORT", icon: <DownloadIcon />, outlined: true, onClick: openExportModal },
            { key: "add", label: "ADD QUESTION", icon: <PlusIcon />, onClick: openAddModal },
          ]}
        >
          <MobileSearchBar value={search ?? ""} onChange={handleSearchChange} placeholder="Search questions" />
          <FilterChips
            options={TABS}
            value={activeTab}
            onChange={(value) => value && handleTabChange(value)}
            allLabel={null}
            ariaLabel="Assign to"
          />
        </MobileListPanel>

        <MobileList
          isLoading={isInitialLoadingQuestions}
          isEmpty={questions.length === 0}
          emptyTitle="No questions found"
          emptyCaption={search ? "Try clearing your search." : "Add a question to get started."}
        >
          <SortableMobileList
            items={questions}
            onReorder={handleReorder}
            disabled={isReordering || !checkPermission("updateQuestion")}
            renderItem={(question, dragHandle, index) => (
              <MobileListRow
                leading={dragHandle}
                title={question.label || "—"}
                badge={<StatusBadge label={captalizeEachWord(question.fieldType) || "—"} />}
                meta={[
                  { key: "order", value: question.displayOrder != null ? `#${question.displayOrder}` : null },
                  { key: "category", value: question.categoryName },
                  { key: "createdBy", value: question.createdBy },
                ]}
                actions={getMobileRowActions(question, index)}
              />
            )}
          />
        </MobileList>

        {hasMoreQuestions && !isInitialLoadingQuestions && (
          <Button outlined onClick={handleLoadMore} disabled={isFetching} className={styles.loadMoreBtn}>
            {isLoadingMoreQuestions ? "LOADING…" : "LOAD MORE"}
          </Button>
        )}
      </div>

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        fallbackFileName="questionnaire"
        fetchExportFile={() =>
          QuestionnaireService.exportQuestions({
            userType: questionUserType,
            search: debouncedSearch || undefined,
          })
        }
      />
    </div>
  );
};

export default QuestionnaireManagement;
